import { Show, type Component } from 'solid-js';

import type { GameStore } from '../state/store';
import { Hud } from './Hud';

export interface AppProps {
  store: GameStore;
  /** Opens the socket. Called once, when the player leaves the title screen. */
  onEnter: () => void;
  /**
   * Walks to the town's return gate and steps through.
   *
   * Passed down rather than reached for: walking belongs to the `Navigator`, which the
   * game loop owns, and a HUD component that steered the player would be a second thing
   * issuing routes. See `GrindPortal`.
   */
  onWalkToGate?: (gate: { x: number; z: number }) => void;
}

/**
 * The shell: title screen, then the HUD.
 *
 * Both live under `#hud-root`, over the same Babylon canvas — the title screen
 * is a scrim over a scene that is already rendering, which is why entering the
 * world is a fade rather than a load.
 */
export const App: Component<AppProps> = (props) => (
  <Show when={props.store.phase() === 'playing'} fallback={<Entering />}>
    <Hud store={props.store} onWalkToGate={props.onWalkToGate} />
  </Show>
);

/**
 * The moment between choosing a character and the first frame arriving.
 *
 * **This used to be the title screen**, which is where it lived when the order was login →
 * roster → title → play. The title comes *first* now, before the engine exists, so showing
 * it again here would be the same screen twice with a character already chosen. What is
 * left is a line over the scene that is already rendering behind it, which is why entering
 * the world is a fade rather than a load.
 */
const Entering: Component = () => (
  <div class="entering" data-testid="entering">
    <p>Entering the world…</p>
  </div>
);
